import axiosInstance from '../api/axios';

export interface TransactionsStatisticSummaryParams {
  from_date?: string | null;
  to_date?: string | null;
  trader_id?: string | null;
  merchant_id?: string | null;
  currency?: string | null;
  payment_system_id?: string | null;
  direction?: string | null;
  min_amount?: string | null;
  max_amount?: string | null;
  entity?: 'trader' | 'merchant';
}

export interface TransactionsStatisticSummaryResponse {
  not_found_transactions: number;
  pending_transactions: number;
  appeal_transactions: number;
  active_traders: number;
  amounts_by_currency?: Array<{
    currency: string;
    turnover: string;
    total_earned: string;
    average_check: string;
    successful_transactions: number;
    usdt_rate: string | null;
  }>;
  total_earned: string;
  total_turnover: string;
  total_transactions: number;
  average_check: string;
  successful_transactions: number;
  failed_transactions: number;
}

export interface TransactionsStatisticSummaryByTraderItem
  extends TransactionsStatisticSummaryResponse {
  trader_id: string;
}

export interface TransactionsStatisticDailyParams {
  trader_id?: string | null;
  merchant_id?: string | null;
  from_date: string; // yyyy-MM-dd
  to_date: string; // yyyy-MM-dd
  currency?: string | null;
  payment_system_id?: string | null;
  direction?: string | null;
  min_amount?: string | null;
  max_amount?: string | null;
  entity?: 'trader' | 'merchant';
}

export interface TransactionsStatisticDailyResponse {
  daily_earned: Array<{
    date: string; // yyyy-MM-dd
    earned: string;
    turnover: string;
    turnover_usdt?: string | null;
    earned_usdt?: string | null;
  }>;
  daily_transactions: Array<{
    date: string; // yyyy-MM-dd
    transaction_count: number;
    not_found_transaction_count: number;
    pending_transaction_count: number;
    appeal_transaction_count: number;
    failed_transaction_count: number;
    successful_transaction_count: number;
  }>;
}

export const transactionsService = {
  getTransactions: async (params?: any) => {
    const response = await axiosInstance.get('/transactions/', { params });
    return response.data;
  },

  getTransaction: async (id: string) => {
    const response = await axiosInstance.get(`/transactions/${id}`);
    return response.data;
  },

  createTransaction: async (data: any) => {
    const response = await axiosInstance.post('/transactions', data);
    return response.data;
  },

  updateTransaction: async (id: string, data: any) => {
    const response = await axiosInstance.put(`/transactions/${id}`, data);
    return response.data;
  },

  deleteTransaction: async (id: string) => {
    const response = await axiosInstance.delete(`/transactions/${id}`);
    return response.data;
  },

  updateTransactionToSuccess: async (transactionId: string, amount: number) => {
    const response = await axiosInstance.patch('/transactions/update-to-success', null, {
      params: {
        transaction_id: transactionId,
        amount
      }
    });
    return response.data;
  },

  updateTransactionToDecline: async (transactionId: string, amount: number) => {
    const response = await axiosInstance.patch('/transactions/update-to-decline', null, {
      params: {
        transaction_id: transactionId,
        amount
      }
    });
    return response.data;
  },

  /**
   * OUT: публикует TransactionOutCompleteToSuccessAdmin (query: payout_invoice_id, amount).
   * На бэкенде проверяется direction=OUT и соответствие FinalOutTransactionCommand.
   */
  updateOutTransactionToSuccess: async (payoutInvoiceId: string, amount: number | string) => {
    const response = await axiosInstance.patch<string>('/transactions/update-out-to-success', null, {
      params: {
        payout_invoice_id: payoutInvoiceId,
        amount,
      },
    });
    return response.data;
  },

  /**
   * OUT: публикует TransactionOutCompleteToDecline (query: payout_invoice_id).
   * На бэкенде проверяется direction=OUT.
   */
  updateOutTransactionToDecline: async (payoutInvoiceId: string) => {
    const response = await axiosInstance.patch<string>('/transactions/update-out-to-decline', null, {
      params: {
        payout_invoice_id: payoutInvoiceId,
      },
    });
    return response.data;
  },

  sendCallback: async (transactionId: string, callbackUrl?: string) => {
    const response = await axiosInstance.post('/transactions/send-callback/', null, {
      params: {
        transaction_id: transactionId,
        callback_url: callbackUrl
      }
    });
    return response.data;
  },

  finalizeSandboxTransaction: async (transactionId: string, targetStatus: 'SUCCESS' | 'DECLINED') => {
    const response = await axiosInstance.post(`/transactions/${transactionId}/sandbox-finalize`, null, {
      params: { target_status: targetStatus },
    });
    return response.data;
  },

  exportTransactions: async (params: {
    format: 'csv' | 'xlsx';
    merchant_id?: string | null;
    trader_id?: string | null;
    search_id?: string | null;
    payment_system_id?: string | null;
    status?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }) => {
    const response = await axiosInstance.get('/transactions/export', {
      params,
      responseType: 'blob',
    });
    return response;
  },

  getStatisticSummary: async (params?: TransactionsStatisticSummaryParams) => {
    const response = await axiosInstance.get<
      TransactionsStatisticSummaryResponse | TransactionsStatisticSummaryByTraderItem[]
    >('/transactions/statistic/summary', { params });
    return response.data;
  },

  getStatisticDaily: async (params: TransactionsStatisticDailyParams) => {
    const response = await axiosInstance.get<TransactionsStatisticDailyResponse>(
      '/transactions/statistic/daily',
      { params },
    );
    return response.data;
  },
}; 
